// src/components/AdminOrderView.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "./AdminLayouts";
import axios from "axios";
import localforage from "localforage";
import API_BASE_URL from "./apiConfig";

const AdminOrderView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
const [editingProductId, setEditingProductId] = useState(null);
const [searchTerm, setSearchTerm] = useState("");
  // Fetch order details from API
useEffect(() => {
  const fetchData = async () => {
    try {
      setLoading(true);
      setOrder(null); // 👈 Reset order when new ID comes in
      const token = await localforage.getItem("jwtToken");

      // 1. Fetch all products
      const productRes = await axios.get(
        `${API_BASE_URL}api/Crackers/GetProductSummary?companyId=3`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const allProducts = productRes.data.statusCode === 200 ? productRes.data.result || [] : [];
      setProducts(allProducts);

      // 2. Fetch order details
      let orderLines = [];
      let orderResData = null;

      if (id) {
        const orderRes = await axios.get(
          `${API_BASE_URL}api/Crackers/GetOrderDetailsByOrderId?OrderId=${id}&CompanyId=3`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (orderRes.data.statusCode === 200) {
          orderLines = orderRes.data.orderLines || [];
          orderResData = orderRes.data;
        } else {
          console.warn("Order API returned non-200:", orderRes.data);
        }
      }

const items = allProducts.map((product) => {
  const line = orderLines.find(l => String(l.prodcutID) === String(product.id));

  const originalPrice = product.prodPrice || 0;
  const discountPrice = product.discountPrice || originalPrice;

  const discountPercent = originalPrice > 0 
    ? Math.round(((originalPrice - discountPrice) / originalPrice) * 100)
    : 0;

  const price = line?.perProdNetAmount ||
                line?.discountedPrice ||
                discountPrice ||
                originalPrice ||
                0;

  return {
    id: product.id,
    productId: product.id,
    name: product.prodName,
    qty: line?.qty || 0,
    pieces: parseInt(product.pieces) || 0, // 👈 Convert to number
    price: price,
    totalPrice: line?.finalTotalAmt || (line?.qty * price) || 0,
    isInOrder: !!line,
    category: product.categoryName || "Uncategorized",
    originalPrice,
    discountPercent: discountPercent > 0 ? discountPercent : 0,
    unit: product.unitName || "" 
  };
});

      // ✅ Only set order once at the end
      setOrder({
        id: orderResData?.orderHeader?.orderId || id || "N/A",
        customerName: `${orderResData?.orderHeader?.firstName || ""} ${orderResData?.orderHeader?.lastName || ""}`.trim() || "N/A",
        orderDate: orderResData?.orderHeader?.orderDate || "",
        items,
        discountAmount: orderResData?.totals?.totalDiscountAmount || 0,
      });

    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load order details.");
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [id]); // 👈 This ensures refetch on ID change

  // Handlers for editing items
const handleItemChange = (itemId, field, value) => {
  const numValue = value === "" ? 0 : parseFloat(value) || 0;

  setOrder((prev) => ({
    ...prev,
    items: prev.items.map((item) => {
      if (item.id === itemId) {
        const updated = { ...item, [field]: numValue };
        updated.totalPrice = updated.qty * updated.price;
        return updated;
      }
      return item;
    }),
  }));
};


  const handleProductSelect = (itemId, productId) => {
  const selectedProduct = products.find(p => p.id === parseInt(productId));
  if (selectedProduct) {
    setOrder((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        if (item.id === itemId) {
          const updated = {
            ...item,
            name: selectedProduct.prodName,
            price: selectedProduct.discountPrice || selectedProduct.prodPrice,
            productId: selectedProduct.id,
          };
          updated.totalPrice = updated.qty * updated.price; // recalc
          return updated;
        }
        return item;
      }),
    }));
  }
};


const handleDeleteItem = (itemId) => {
  setOrder((prev) => ({
    ...prev,
    items: prev.items.map((item) =>
      item.id === itemId ? { ...item, qty: 0, totalPrice: 0 } : item
    ),
  }));
};

  const handleAddItem = () => {
    setOrder((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { id: Date.now(), name: "Select Product", qty: 1, price: 0, totalPrice: 0, productId: "" },
      ],
    }));
  };

const handleSave = async () => {
  if (!order || !id) return;

  try {
    const productsPayload = order.items
      .filter(item => item.qty > 0)
      .map(item => {
        const price = item.price;
        const finalTotalAmt = item.qty * price;

        return {
          productID: item.productId,
          productName: item.name,
          productDesc: "",
          productPrice: price,
          qty: item.qty,
          totalPrice: item.qty * price,
          discountedPrice: price,
          totalDiscPrice: item.qty * price,
          perProdNetAmount: price,
          finalTotalAmt: finalTotalAmt,
        };
      });

    const payload = {
      orderId: id,
      companyId: 3,
      products: productsPayload,
    };

    const token = await localforage.getItem("jwtToken");

    await axios.post(
      `${API_BASE_URL}api/Crackers/UpdateOrderProducts`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    alert(`Order ${id} Updated successfully!`);

    // ✅ REFRESH THE ORDER DATA FROM SERVER TO UPDATE UI
    // Instead of resetting state, trigger a re-fetch
    // We can do this by forcing a reload of the component or refetching data.
    // Since we use `useEffect` on [id], we can simply re-fetch by doing nothing — but we need to trigger it.

    // One simple way: set loading state and fetch again.
    setLoading(true);
    const tokenAfterSave = await localforage.getItem("jwtToken");

    // Re-fetch order details
    const orderRes = await axios.get(
      `${API_BASE_URL}api/Crackers/GetOrderDetailsByOrderId?OrderId=${id}&CompanyId=3`,
      { headers: { Authorization: `Bearer ${tokenAfterSave}` } }
    );

    if (orderRes.data.statusCode === 200) {
      let orderLines = orderRes.data.orderLines || [];
      let orderResData = orderRes.data;

      // Reconstruct the items array using the same logic as in useEffect
      const updatedItems = products.map((product) => {
        const line = orderLines.find(l => String(l.prodcutID) === String(product.id));

        const originalPrice = product.prodPrice || 0;
        const discountPrice = product.discountPrice || originalPrice;

        const discountPercent = originalPrice > 0 
          ? Math.round(((originalPrice - discountPrice) / originalPrice) * 100)
          : 0;

        const price = line?.perProdNetAmount ||
                      line?.discountedPrice ||
                      discountPrice ||
                      originalPrice ||
                      0;

        return {
          id: product.id,
          productId: product.id,
          name: product.prodName,
          qty: line?.qty || 0,
          pieces: parseInt(product.pieces) || 0,
          price: price,
          totalPrice: line?.finalTotalAmt || (line?.qty * price) || 0,
          isInOrder: !!line,
          category: product.categoryName || "Uncategorized",
          originalPrice,
          discountPercent: discountPercent > 0 ? discountPercent : 0,
          unit: product.unitName || "" 
        };
      });

      setOrder({
        id: orderResData?.orderHeader?.orderId || id || "N/A",
        customerName: `${orderResData?.orderHeader?.firstName || ""} ${orderResData?.orderHeader?.lastName || ""}`.trim() || "N/A",
        orderDate: orderResData?.orderHeader?.orderDate || "",
        items: updatedItems,
        discountAmount: orderResData?.totals?.totalDiscountAmount || 0,
      });
    } else {
      console.warn("Order API returned non-200 after save:", orderRes.data);
    }

  } catch (err) {
    console.error("Save failed:", err);
    alert("Failed to save order: " + (err.response?.data?.message || err.message));
  } finally {
    setLoading(false);
  }
};


  const formatDate = (isoDate) => {
    if (!isoDate) return "-";
    return new Date(isoDate).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

// Subtotal: sum of (qty × price) for all items
const totalAmount = order?.items.reduce((sum, item) => sum + (item.qty * item.price), 0) || 0;

// Calculate discount based on difference (if needed)
// But if you want to show discount even if backend sends wrong value:
const itemsWithDiscount = order?.items.filter(item => {
  const original = item.price;
  const effective = item.price;
  return effective < original;
});

const calculatedDiscount = order?.items.reduce((sum, item) => {
  const originalTotal = item.qty * (item.price); // assume original was higher
  const currentTotal = item.qty * item.price;
  return sum + (originalTotal - currentTotal);
}, 0);

// Use this instead of order.discountAmount
const finalTotal = Math.max(0, totalAmount - calculatedDiscount);

  if (loading) return <Layout><div style={styles.loading}>Loading order details...</div></Layout>;
  if (error) return <Layout><div style={styles.error}>{error}</div></Layout>;

  return (
    <>
      <Layout />
      <div style={styles.container}>

<div style={styles.summaryHeader}>
  <div style={{ display: "flex", alignItems: "center", gap: "40px", flex: 1 }}>
    <h3 style={{ ...styles.sectionHeader, margin: 0, fontWeight: "500" }}>
      {order.id}
    </h3>

    {/* Search Input */}
    <div style={{ position: "relative", flex: 1, maxWidth: "300px" }}>
      <input
        type="text"
        placeholder="Search products..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{
          width: "100%",
          padding: "6px 10px 6px 28px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          fontSize: "13px",
          background: "#fff",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23666' viewBox='0 0 16 16'%3E%3Cpath d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.125 3.125a1 1 0 1 0 1.414-1.414l-3.125-3.125zm-4.742 0a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z'%3E%3C/path%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "8px center",
          backgroundSize: "12px",
        }}
      />
    </div>

    {/* Item Count Badge - Button Style */}
    <div
      style={{
        backgroundColor: "#532a51ff",
        color: "white",
        padding: "6px 12px",
        borderRadius: "6px",
        fontSize: "13px",
        fontWeight: "bold",
        whiteSpace: "nowrap",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        marginLeft:"10px"
      }}
    >
      {order?.items.filter(i => i.qty > 0).length} Items
    </div>
  </div>

  {/* Total & Save Button */}
  <div style={styles.summaryAmounts}>
    {calculatedDiscount > 0 && (
      <span>Discount: -₹{calculatedDiscount.toFixed(2)}</span>
    )}
    <strong>Total: ₹{finalTotal.toFixed(2)}</strong>
    <button onClick={handleSave} style={styles.saveButton}>
      Save Changes
    </button>
  </div>
</div>

               {/* Order Items Table */}
        <div style={styles.section}>
          <div style={styles.tableHeader}>
          </div>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '5%' }}>Sno</th>
                  <th style={{ ...styles.th, width: '60%' }}>Product</th>
                  <th style={{ ...styles.th, width: '10%', textAlign: 'right' }}>Price (₹)</th>
                  <th style={{ ...styles.th, width: '10%', textAlign: 'center' }}>Qty</th>
                  <th style={{ ...styles.th, width: '15%', textAlign: 'right' }}>Total (₹)</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  if (!order?.items || order.items.length === 0) {
                    return (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center", padding: "16px", color: "#999" }}>
                          No products available.
                        </td>
                      </tr>
                    );
                  }

                  // 🔍 Filter items by search term (name or category)
                  const filteredItems = searchTerm
                    ? order.items.filter(item =>
                        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        item.category.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                    : order.items;

                  // 📂 Group by category
                  const grouped = filteredItems.reduce((acc, item) => {
                    const category = item.category || "Uncategorized";
                    if (!acc[category]) acc[category] = [];
                    acc[category].push(item);
                    return acc;
                  }, {});

                  const categoryEntries = Object.entries(grouped);

                  if (categoryEntries.length === 0) {
                    return (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center", padding: "16px", color: "#999" }}>
                          No products match your search.
                        </td>
                      </tr>
                    );
                  }

                  return categoryEntries.map(([category, itemsInCat]) => (
                    <React.Fragment key={category}>
                      {/* Category Header */}
                      <tr>
                        <td colSpan="5" style={{
                          backgroundColor: "#e9ecef",
                          fontWeight: "bold",
                          color: "#444",
                          fontSize: "12px",
                          padding: "6px 10px",
                          textAlign: "left",
                        }}>
                          {category}
                        </td>
                      </tr>
                      {/* Product Rows */}
                      {itemsInCat.map((item, index) => (
                        <tr key={item.id}>
                          <td style={{ ...styles.tdCenter, width: '5%' }}>{index + 1}</td>
                          <td style={{ ...styles.td, width: '60%' }}>
                            <div style={{ fontWeight: "500", fontSize: "13px" }}>
                              {/* Product Name */}
                              {item.name}

                              {/* (50 Pieces) */}
                              {item.pieces > 0 && (
                                <span style={{ marginLeft: "6px", color: "#930808ff", fontSize: "11px" }}>
                                  ({item.pieces} Pieces)
                                </span>
                              )}

                              {/* • Packet */}
                              {item.unit && (
                                <>
                                  <span style={{ margin: "0 6px", color: "#890606ff" }}>•</span>
                                  <span style={{ color: "#1a73e8", fontSize: "11px" }}>{item.unit}</span>
                                </>
                              )}

                              {/* • ₹50.00 → ₹25.00 (50% off) */}
                              {item.discountPercent > 0 && (
                                <>
                                  <span style={{ margin: "0 6px", color: "#999" }}>•</span>
                                  <span style={{ fontSize: "11px", color: "#555" }}>
                                    <del>₹{item.originalPrice.toFixed(2)}</del> →{" "}
                                    <strong>₹{item.price.toFixed(2)}</strong>{" "}
                                    <span style={{
                                      backgroundColor: "#e8f5e9",
                                      color: "#2e7d32",
                                      padding: "1px 6px",
                                      borderRadius: "12px",
                                      fontSize: "10px",
                                      fontWeight: "500",
                                      marginLeft: "4px"
                                    }}>
                                      {item.discountPercent}% off
                                    </span>
                                  </span>
                                </>
                              )} 
                            </div>
                          </td>
                          <td style={{ ...styles.tdRight, width: '10%' }}>
                            ₹{(item.price).toFixed(2)}
                          </td>
                          <td style={{ ...styles.tdCenter, width: '10%' }}>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={item.qty === 0 ? "" : item.qty}
                              onChange={(e) => handleItemChange(item.id, "qty", e.target.value)}
                              style={{ ...styles.compactInput, width: "60px" }}
                              placeholder=""
                            />
                          </td>
                          <td style={{ ...styles.tdRight, width: '15%' }}>
                            ₹{(item.qty * item.price).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>
        {/* <div style={styles.buttonContainer}>
          <button onClick={() => navigate(-1)} style={styles.backButton}>
            Back
          </button>
          <button onClick={handleSave} style={styles.saveButton}>
            Save Changes
          </button>
        </div> */}
      </div>
    </>
  );
};

const styles = {
  container: {
    padding: "15px",
    maxWidth: "1200px",
    margin: "0 auto",
    fontSize: "13px",
  },
  header: {
    color: "#532a51ff",
    marginBottom: "15px",
    fontSize: "18px",
    fontWeight: "600",
  },summaryHeader: {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "15px",
  padding: "10px",
  backgroundColor: "#f8f9fa",
  borderRadius: "6px",
  border: "1px solid #ddd",
},
summaryAmounts: {
  display: "flex",
  alignItems: "center",
  gap: "15px",
  fontSize: "13px",
},

  section: {
    backgroundColor: "#fff",
    borderRadius: "6px",
    padding: "15px",
    marginBottom: "15px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  sectionHeader: {
    color: "#1a237e",
    marginBottom: "12px",
    fontSize: "14px",
    fontWeight: "500",
  },
  formRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
    marginBottom: "12px",
  },
  formGroup: {
    flex: "1",
    minWidth: "150px",
  },
  label: {
    display: "block",
    marginBottom: "3px",
    fontWeight: "500",
    color: "#555",
    fontSize: "12px",
  },
  compactField: {
    padding: "6px 8px",
    border: "1px solid #e0e0e0",
    borderRadius: "3px",
    backgroundColor: "#f8f9fa",
    fontSize: "12px",
    minHeight: "30px",
    display: "flex",
    alignItems: "center",
  },

tableContainer: {
  maxHeight: "350px",   // fixed max height
  overflowY: "auto",    // vertical scroll
  overflowX: "auto",    // keep horizontal scroll too
  fontSize: "12px",
  border: "1px solid #ddd",
}
,
  table: {
    width: "100%",
    borderCollapse: "collapse",
   
  },
th: {
  backgroundColor: "#532a51ff",
  color: "#fff",
  padding: "5px 10px",
  textAlign: "left",
  fontSize: "11px",
  fontWeight: "500",
  position: "sticky",
  top: 0,
  zIndex: 2
},

  td: {
    padding: "4px 6px",   // 🔽 was 8px
    borderBottom: "1px solid #eee",
    fontSize: "11px",
  },
  tdCenter: {
    textAlign: "center",
    padding: "4px 6px",   // 🔽 reduced
    borderBottom: "1px solid #eee",
    fontSize: "11px",
  },
  tdRight: {
    textAlign: "right",
    padding: "4px 6px",   // 🔽 reduced
    borderBottom: "1px solid #eee",
    fontSize: "11px",
  },
  tdRightBold: {
    textAlign: "right",
    padding: "4px 6px",   // 🔽 reduced
    borderBottom: "1px solid #eee",
    fontSize: "11px",
    fontWeight: "bold",
  },
  compactInput: {
    width: "100%",
    padding: "3px 5px",   // 🔽 smaller input padding
    border: "1px solid #ddd",
    borderRadius: "3px",
    backgroundColor: "#fff",
    fontSize: "11px",
    height: "24px",       // 🔽 smaller height
  },
  tdRightBold: {
    textAlign: "right",
    padding: "8px",
    borderBottom: "1px solid #eee",
    fontSize: "12px",
    fontWeight: "bold",
  },
  deleteButton: {
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    padding: "2px 6px",
    borderRadius: "3px",
    cursor: "pointer",
    fontSize: "12px",
  },

  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
     padding: "10px 10px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "15px",
    gap: "10px",
  },
  backButton: {
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    padding: "6px 15px",
    borderRadius: "3px",
    cursor: "pointer",
    fontSize: "12px",
  },
  saveButton: {
    backgroundColor: "#532a51ff",
    color: "#fff",
    border: "none",
    padding: "6px 15px",
    borderRadius: "3px",
    cursor: "pointer",
    fontSize: "12px",
  },
  loading: {
    textAlign: "center",
    padding: "20px",
    color: "#555",
  },
  error: {
    color: "red",
    textAlign: "center",
    padding: "20px",
  },
  
};

export default AdminOrderView;